import React, { useState } from 'react';
import './PlannerPage.css';

const PlannerPage: React.FC = () => {
  const [tasks, setTasks] = useState<Array<{id: number, text: string, completed: boolean, date: string}>>([]);
  const [newTask, setNewTask] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  
  // Goals state
  const [goals, setGoals] = useState<Array<{id: number, title: string, description: string, steps: string[], completed: boolean}>>([]);
  const [showGoalsModal, setShowGoalsModal] = useState(false);
  const [newGoal, setNewGoal] = useState({title: '', description: '', steps: ['']});
  
  // Weekly planning state
  const [weeklyReflections, setWeeklyReflections] = useState<Array<{id: number, week: string, reflection: string, improvements: string}>>([]);
  const [showReflectionModal, setShowReflectionModal] = useState(false);
  const [newReflection, setNewReflection] = useState({reflection: '', improvements: ''});
  
  // Habits state
  const [habits, setHabits] = useState<Array<{id: number, name: string, description: string, frequency: string, completed: boolean, streak: number}>>([]);
  const [showHabitsModal, setShowHabitsModal] = useState(false);
  const [newHabit, setNewHabit] = useState({name: '', description: '', frequency: 'daily'});
  
  // Budget state
  const [budget, setBudget] = useState<Array<{id: number, category: string, amount: number, type: 'income' | 'expense', date: string, description: string}>>([]);
  const [showBudgetModal, setShowBudgetModal] = useState(false);
  const [newBudgetItem, setNewBudgetItem] = useState({category: '', amount: '', type: 'expense' as 'income' | 'expense', description: ''});
  
  // Shopping lists state
  const [shoppingLists, setShoppingLists] = useState<Array<{id: number, name: string, items: Array<{id: number, name: string, completed: boolean, price?: number}>}>>([]);
  const [showShoppingModal, setShowShoppingModal] = useState(false);
  const [newShoppingList, setNewShoppingList] = useState({name: '', items: [{name: '', price: ''}]});

  // Travel planning state
  const [trips, setTrips] = useState<Array<{id: number, title: string, destination: string, startDate: string, endDate: string, budget: number, notes: string, checklist: Array<{id: number, item: string, completed: boolean}>, itinerary: Array<{id: number, day: number, activity: string, time: string, location: string, notes: string}>, photos: Array<{id: number, url: string, caption: string, date: string}>}>>([]);
  const [showTravelModal, setShowTravelModal] = useState(false);
  const [newTrip, setNewTrip] = useState({title: '', destination: '', startDate: '', endDate: '', budget: '', notes: '', checklist: [{item: '', completed: false}], itinerary: [{day: 1, activity: '', time: '', location: '', notes: ''}]});

  // Gamification state
  const [achievements, setAchievements] = useState<Array<{id: number, title: string, description: string, icon: string, category: string, unlocked: boolean, unlockedDate?: string, points: number}>>([
    {id: 1, title: 'Первые шаги', description: 'Создайте первую задачу', icon: '🎯', category: 'tasks', unlocked: false, points: 10},
    {id: 2, title: 'Планировщик', description: 'Выполните 10 задач', icon: '📝', category: 'tasks', unlocked: false, points: 25},
    {id: 3, title: 'Целеустремленный', description: 'Достигните первой цели', icon: '🎯', category: 'goals', unlocked: false, points: 50},
    {id: 4, title: 'Привычка', description: 'Соблюдайте привычку 7 дней подряд', icon: '🔥', category: 'habits', unlocked: false, points: 30},
    {id: 5, title: 'Путешественник', description: 'Спланируйте первую поездку', icon: '✈️', category: 'travel', unlocked: false, points: 40},
    {id: 6, title: 'Финансист', description: 'Ведите бюджет месяц', icon: '💰', category: 'budget', unlocked: false, points: 35},
    {id: 7, title: 'Супергерой', description: 'Выполните 100 задач', icon: '🦸', category: 'tasks', unlocked: false, points: 100},
    {id: 8, title: 'Мастер привычек', description: 'Соблюдайте привычку 30 дней', icon: '👑', category: 'habits', unlocked: false, points: 75}
  ]);
  
  const [userLevel, setUserLevel] = useState(1);
  const [userPoints, setUserPoints] = useState(0);
  const [userStreak, setUserStreak] = useState(0);
  const [lastActivityDate, setLastActivityDate] = useState(new Date().toDateString());
  
  const [challenges, setChallenges] = useState<Array<{id: number, title: string, description: string, type: string, target: number, current: number, completed: boolean, startDate: string, endDate: string, reward: number}>>([
    {id: 1, title: 'Неделя продуктивности', description: 'Выполните 20 задач за неделю', type: 'tasks', target: 20, current: 0, completed: false, startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], reward: 50},
    {id: 2, title: 'Привычка на 7 дней', description: 'Соблюдайте любую привычку 7 дней подряд', type: 'habits', target: 7, current: 0, completed: false, startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], reward: 75},
    {id: 3, title: 'Целеустремленность', description: 'Достигните 3 целей за месяц', type: 'goals', target: 3, current: 0, completed: false, startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], reward: 100}
  ]);

  // Integrations state
  const [calendarIntegration, setCalendarIntegration] = useState({
    googleCalendar: false,
    appleCalendar: false,
    syncEvents: false
  });

  const [fitnessIntegration, setFitnessIntegration] = useState({
    appleHealth: false,
    googleFit: false,
    syncSteps: false,
    syncHeartRate: false,
    syncSleep: false
  });

  const [weatherData, setWeatherData] = useState({
    currentTemp: 22,
    condition: 'солнечно',
    location: 'Москва',
    forecast: [
      {day: 'Сегодня', temp: 22, condition: 'солнечно'},
      {day: 'Завтра', temp: 18, condition: 'облачно'},
      {day: 'Послезавтра', temp: 15, condition: 'дождь'}
    ]
  });

  const [socialSharing, setSocialSharing] = useState({
    shareAchievements: false,
    shareProgress: false,
    shareGoals: false,
    platforms: {
      instagram: false,
      facebook: false,
      twitter: false,
      telegram: false
    }
  });

  // Gamification functions
  const updateStreak = () => {
    const today = new Date().toDateString();
    if (lastActivityDate !== today) {
      const yesterday = new Date(Date.now() - 24 * 60 * 60 * 1000).toDateString();
      if (lastActivityDate === yesterday) {
        setUserStreak(prev => prev + 1);
      } else {
        setUserStreak(1);
      }
      setLastActivityDate(today);
    }
  };

  const addPoints = (points: number) => {
    setUserPoints(prev => {
      const newPoints = prev + points;
      const newLevel = Math.floor(newPoints / 100) + 1;
      setUserLevel(newLevel);
      return newPoints;
    });
  };

  const checkAchievements = (action: string, data?: any) => {
    const today = new Date().toISOString();
    
    achievements.forEach(achievement => {
      if (!achievement.unlocked) {
        let shouldUnlock = false;
        
        switch (achievement.category) {
          case 'tasks':
            if (action === 'task_created' && tasks.length === 0) shouldUnlock = true;
            if (action === 'task_completed' && tasks.filter(t => t.completed).length >= 10) shouldUnlock = true;
            if (action === 'task_completed' && tasks.filter(t => t.completed).length >= 100) shouldUnlock = true;
            break;
          case 'goals':
            if (action === 'goal_completed' && goals.filter(g => g.completed).length >= 1) shouldUnlock = true;
            break;
          case 'habits':
            if (action === 'habit_completed' && habits.some(h => h.streak >= 7)) shouldUnlock = true;
            if (action === 'habit_completed' && habits.some(h => h.streak >= 30)) shouldUnlock = true;
            break;
          case 'travel':
            if (action === 'trip_created' && trips.length >= 1) shouldUnlock = true;
            break;
          case 'budget':
            if (action === 'budget_entry' && budget.length >= 30) shouldUnlock = true;
            break;
        }
        
        if (shouldUnlock) {
          setAchievements(prev => prev.map(a => 
            a.id === achievement.id 
              ? { ...a, unlocked: true, unlockedDate: today }
              : a
          ));
          addPoints(achievement.points);
        }
      }
    });
  };

  const checkChallenges = (action: string) => {
    setChallenges(prev => prev.map(challenge => {
      if (!challenge.completed) {
        let newCurrent = challenge.current;
        
        switch (challenge.type) {
          case 'tasks':
            if (action === 'task_completed') newCurrent = Math.min(challenge.current + 1, challenge.target);
            break;
          case 'habits':
            if (action === 'habit_completed') {
              const maxStreak = Math.max(...habits.map(h => h.streak), 0);
              newCurrent = Math.min(maxStreak, challenge.target);
            }
            break;
          case 'goals':
            if (action === 'goal_completed') newCurrent = Math.min(challenge.current + 1, challenge.target);
            break;
        }
        
        if (newCurrent >= challenge.target && !challenge.completed) {
          addPoints(challenge.reward);
          return { ...challenge, current: newCurrent, completed: true };
        }
        
        return { ...challenge, current: newCurrent };
      }
      return challenge;
    }));
  };

  const addTask = () => {
    if (newTask.trim()) {
      const task = {
        id: Date.now(),
        text: newTask.trim(),
        completed: false,
        date: selectedDate
      };
      setTasks([...tasks, task]);
      setNewTask('');
      updateStreak();
      checkAchievements('task_created');
    }
  };

  const toggleTask = (id: number) => {
    const task = tasks.find(t => t.id === id);
    const wasCompleted = task?.completed;
    
    setTasks(tasks.map(task => 
      task.id === id ? { ...task, completed: !task.completed } : task
    ));
    
    if (!wasCompleted) {
      checkAchievements('task_completed');
      checkChallenges('task_completed');
    }
  };

  const deleteTask = (id: number) => {
    setTasks(tasks.filter(task => task.id !== id));
  };

  const getTasksForDate = (date: string) => {
    return tasks.filter(task => task.date === date);
  };

  const currentTasks = getTasksForDate(selectedDate);

  // Goals functions
  const addGoal = () => {
    if (newGoal.title.trim()) {
      const goal = {
        id: Date.now(),
        title: newGoal.title.trim(),
        description: newGoal.description.trim(),
        steps: newGoal.steps.filter(step => step.trim()),
        completed: false
      };
      setGoals([...goals, goal]);
      setNewGoal({title: '', description: '', steps: ['']});
      setShowGoalsModal(false);
    }
  };

  const addGoalStep = () => {
    setNewGoal({...newGoal, steps: [...newGoal.steps, '']});
  };

  const updateGoalStep = (index: number, value: string) => {
    const newSteps = [...newGoal.steps];
    newSteps[index] = value;
    setNewGoal({...newGoal, steps: newSteps});
  };

  // Reflection functions
  const addReflection = () => {
    if (newReflection.reflection.trim()) {
      const week = new Date().toISOString().split('T')[0];
      const reflection = {
        id: Date.now(),
        week,
        reflection: newReflection.reflection.trim(),
        improvements: newReflection.improvements.trim()
      };
      setWeeklyReflections([...weeklyReflections, reflection]);
      setNewReflection({reflection: '', improvements: ''});
      setShowReflectionModal(false);
    }
  };

  // Habits functions
  const addHabit = () => {
    if (newHabit.name.trim()) {
      const habit = {
        id: Date.now(),
        name: newHabit.name.trim(),
        description: newHabit.description.trim(),
        frequency: newHabit.frequency,
        completed: false,
        streak: 0
      };
      setHabits([...habits, habit]);
      setNewHabit({name: '', description: '', frequency: 'daily'});
      setShowHabitsModal(false);
    }
  };

  const toggleHabit = (id: number) => {
    const habit = habits.find(h => h.id === id);
    const wasCompleted = habit?.completed;
    
    setHabits(habits.map(habit => 
      habit.id === id ? { ...habit, completed: !habit.completed, streak: habit.completed ? habit.streak - 1 : habit.streak + 1 } : habit
    ));
    
    if (!wasCompleted) {
      checkAchievements('habit_completed');
      checkChallenges('habit_completed');
    }
  };

  // Budget functions
  const addBudgetItem = () => {
    if (newBudgetItem.category.trim() && newBudgetItem.amount) {
      const budgetItem = {
        id: Date.now(),
        category: newBudgetItem.category.trim(),
        amount: parseFloat(newBudgetItem.amount),
        type: newBudgetItem.type,
        date: new Date().toISOString().split('T')[0],
        description: newBudgetItem.description.trim()
      };
      setBudget([...budget, budgetItem]);
      setNewBudgetItem({category: '', amount: '', type: 'expense', description: ''});
      setShowBudgetModal(false);
      checkAchievements('budget_entry');
    }
  };

  // Shopping lists functions
  const addShoppingList = () => {
    if (newShoppingList.name.trim()) {
      const items = newShoppingList.items
        .filter(item => item.name.trim())
        .map((item, index) => ({
          id: index,
          name: item.name.trim(),
          completed: false,
          price: item.price ? parseFloat(item.price) : undefined
        }));
      
      const shoppingList = {
        id: Date.now(),
        name: newShoppingList.name.trim(),
        items
      };
      setShoppingLists([...shoppingLists, shoppingList]);
      setNewShoppingList({name: '', items: [{name: '', price: ''}]});
      setShowShoppingModal(false);
    }
  };

  const addShoppingItem = () => {
    setNewShoppingList({...newShoppingList, items: [...newShoppingList.items, {name: '', price: ''}]});
  };

  const updateShoppingItem = (index: number, field: 'name' | 'price', value: string) => {
    const newItems = [...newShoppingList.items];
    newItems[index] = {...newItems[index], [field]: value};
    setNewShoppingList({...newShoppingList, items: newItems});
  };

  // Travel planning functions
  const addTrip = () => {
    if (newTrip.title.trim() && newTrip.destination.trim()) {
      const checklist = newTrip.checklist
        .filter(item => item.item.trim())
        .map((item, index) => ({
          id: index,
          item: item.item.trim(),
          completed: item.completed
        }));
      
      const itinerary = newTrip.itinerary
        .filter(day => day.activity.trim())
        .map((day, index) => ({
          id: index,
          day: day.day,
          activity: day.activity.trim(),
          time: day.time.trim(),
          location: day.location.trim(),
          notes: day.notes.trim()
        }));

      const trip = {
        id: Date.now(),
        title: newTrip.title.trim(),
        destination: newTrip.destination.trim(),
        startDate: newTrip.startDate,
        endDate: newTrip.endDate,
        budget: parseFloat(newTrip.budget) || 0,
        notes: newTrip.notes.trim(),
        checklist,
        itinerary,
        photos: []
      };
      setTrips([...trips, trip]);
      setNewTrip({title: '', destination: '', startDate: '', endDate: '', budget: '', notes: '', checklist: [{item: '', completed: false}], itinerary: [{day: 1, activity: '', time: '', location: '', notes: ''}]});
      setShowTravelModal(false);
      checkAchievements('trip_created');
    }
  };

  const addChecklistItem = () => {
    setNewTrip({...newTrip, checklist: [...newTrip.checklist, {item: '', completed: false}]});
  };

  const updateChecklistItem = (index: number, value: string) => {
    const newChecklist = [...newTrip.checklist];
    newChecklist[index] = {...newChecklist[index], item: value};
    setNewTrip({...newTrip, checklist: newChecklist});
  };

  const addItineraryDay = () => {
    const lastDay = Math.max(...newTrip.itinerary.map(day => day.day), 0);
    setNewTrip({...newTrip, itinerary: [...newTrip.itinerary, {day: lastDay + 1, activity: '', time: '', location: '', notes: ''}]});
  };

  const updateItineraryItem = (index: number, field: string, value: string) => {
    const newItinerary = [...newTrip.itinerary];
    newItinerary[index] = {...newItinerary[index], [field]: value};
    setNewTrip({...newTrip, itinerary: newItinerary});
  };

  const toggleChecklistItem = (tripId: number, itemId: number) => {
    setTrips(trips.map(trip => 
      trip.id === tripId 
        ? {
            ...trip,
            checklist: trip.checklist.map(item =>
              item.id === itemId ? { ...item, completed: !item.completed } : item
            )
          }
        : trip
    ));
  };

  // Integration functions
  const syncWithCalendar = (platform: 'google' | 'apple') => {
    if (platform === 'google') {
      setCalendarIntegration(prev => ({...prev, googleCalendar: true, syncEvents: true}));
      // Simulate Google Calendar integration
      alert('Интеграция с Google Calendar активирована! События будут синхронизироваться автоматически.');
    } else {
      setCalendarIntegration(prev => ({...prev, appleCalendar: true, syncEvents: true}));
      // Simulate Apple Calendar integration
      alert('Интеграция с Apple Calendar активирована! События будут синхронизироваться автоматически.');
    }
  };

  const syncWithFitness = (platform: 'apple' | 'google') => {
    if (platform === 'apple') {
      setFitnessIntegration(prev => ({
        ...prev, 
        appleHealth: true, 
        syncSteps: true, 
        syncHeartRate: true, 
        syncSleep: true
      }));
      alert('Интеграция с Apple Health активирована! Данные о здоровье будут синхронизироваться.');
    } else {
      setFitnessIntegration(prev => ({
        ...prev, 
        googleFit: true, 
        syncSteps: true, 
        syncHeartRate: true, 
        syncSleep: true
      }));
      alert('Интеграция с Google Fit активирована! Данные о здоровье будут синхронизироваться.');
    }
  };

  const shareAchievement = (platform: string, achievement: any) => {
    const message = `🎉 Я получила достижение "${achievement.title}" в Girl Planner! ${achievement.points} очков! #GirlPlanner #Достижения`;
    
    switch (platform) {
      case 'instagram':
        alert(`Поделиться в Instagram:\n${message}`);
        break;
      case 'facebook':
        alert(`Поделиться в Facebook:\n${message}`);
        break;
      case 'twitter':
        alert(`Поделиться в Twitter:\n${message}`);
        break;
      case 'telegram':
        alert(`Поделиться в Telegram:\n${message}`);
        break;
      default:
        navigator.clipboard.writeText(message);
        alert('Текст скопирован в буфер обмена!');
    }
  };

  const getWeatherIcon = (condition: string) => {
    switch (condition) {
      case 'солнечно': return '☀️';
      case 'облачно': return '☁️';
      case 'дождь': return '🌧️';
      case 'снег': return '❄️';
      default: return '🌤️';
    }
  };

  return (
    <div className="planner-page">
      <div className="page">
        <div className="page-header">
          <h1 className="page-title">Планер</h1>
          <p className="page-subtitle">Организуй свою жизнь и достигни всех целей</p>
        </div>
        
        <div className="page-content">
          {/* Gamification Overview */}
          <div className="gamification-overview">
            <div className="user-stats">
              <div className="stat-card level-card">
                <div className="stat-icon">⭐</div>
                <div className="stat-info">
                  <div className="stat-value">Уровень {userLevel}</div>
                  <div className="stat-label">{userPoints} очков</div>
                </div>
              </div>
              <div className="stat-card streak-card">
                <div className="stat-icon">🔥</div>
                <div className="stat-info">
                  <div className="stat-value">{userStreak}</div>
                  <div className="stat-label">дней подряд</div>
                </div>
              </div>
              <div className="stat-card achievements-card">
                <div className="stat-icon">🏆</div>
                <div className="stat-info">
                  <div className="stat-value">{achievements.filter(a => a.unlocked).length}</div>
                  <div className="stat-label">достижений</div>
                </div>
              </div>
            </div>
            
            <div className="recent-achievements">
              <h3>Последние достижения</h3>
              <div className="achievements-preview">
                {achievements.filter(a => a.unlocked).slice(-3).map(achievement => (
                  <div key={achievement.id} className="achievement-badge unlocked">
                    <span className="achievement-icon">{achievement.icon}</span>
                    <div className="achievement-info">
                      <div className="achievement-title">{achievement.title}</div>
                      <div className="achievement-points">+{achievement.points} очков</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">Ежедневное планирование</h2>
            
            <div className="daily-tasks-container">
              <div className="date-selector">
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="date-input"
                />
              </div>
              
              <div className="task-input-container">
                <input
                  type="text"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTask()}
                  placeholder="Добавить задачу..."
                  className="task-input"
                />
                <button onClick={addTask} className="add-task-btn">+</button>
              </div>

              <div className="tasks-list">
                {currentTasks.length === 0 ? (
                  <div className="empty-state">
                    <p>Нет задач на этот день</p>
                  </div>
                ) : (
                  currentTasks.map(task => (
                    <div key={task.id} className={`task-item ${task.completed ? 'completed' : ''}`}>
                      <button
                        onClick={() => toggleTask(task.id)}
                        className={`task-checkbox ${task.completed ? 'checked' : ''}`}
                      >
                        {task.completed && '✓'}
                      </button>
                      <span className={`task-text ${task.completed ? 'completed' : ''}`}>
                        {task.text}
                      </span>
                      <button
                        onClick={() => deleteTask(task.id)}
                        className="delete-task-btn"
                      >
                        🗑️
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Goals Section */}
            <div className="goals-section">
              <div className="goals-header">
                <h3>Цели и мечты</h3>
                <button onClick={() => setShowGoalsModal(true)} className="btn btn-primary">Добавить цель</button>
              </div>
              
              <div className="goals-list">
                {goals.length === 0 ? (
                  <div className="empty-state">
                    <p>У вас пока нет целей. Добавьте первую!</p>
                  </div>
                ) : (
                  goals.map(goal => (
                    <div key={goal.id} className="goal-item">
                      <div className="goal-header">
                        <h4>{goal.title}</h4>
                        <span className={`goal-status ${goal.completed ? 'completed' : 'active'}`}>
                          {goal.completed ? 'Выполнено' : 'Активно'}
                        </span>
                      </div>
                      <p className="goal-description">{goal.description}</p>
                      {goal.steps.length > 0 && (
                        <div className="goal-steps">
                          <h5>Шаги:</h5>
                          <ul>
                            {goal.steps.map((step, index) => (
                              <li key={index}>{step}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Progress Section */}
            <div className="progress-section">
              <h3>Прогресс</h3>
              <div className="progress-stats">
                <div className="stat-item">
                  <span className="stat-number">{tasks.filter(t => t.completed).length}</span>
                  <span className="stat-label">Выполнено задач</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{goals.filter(g => g.completed).length}</span>
                  <span className="stat-label">Достигнуто целей</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{habits.reduce((sum, h) => sum + h.streak, 0)}</span>
                  <span className="stat-label">Дней привычек</span>
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">Еженедельное планирование</h2>
            
            {/* Weekly Calendar */}
            <div className="weekly-calendar">
              <h3>Недельный обзор</h3>
              <div className="calendar-grid">
                {['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'].map((day, index) => {
                  const date = new Date();
                  const dayOfWeek = date.getDay();
                  const monday = new Date(date.setDate(date.getDate() - dayOfWeek + 1));
                  const dayDate = new Date(monday);
                  dayDate.setDate(monday.getDate() + index);
                  const dateStr = dayDate.toISOString().split('T')[0];
                  const dayTasks = getTasksForDate(dateStr);
                  
                  return (
                    <div key={index} className="calendar-day">
                      <div className="day-header">
                        <span className="day-name">{day}</span>
                        <span className="day-number">{dayDate.getDate()}</span>
                      </div>
                      <div className="day-tasks">
                        {dayTasks.slice(0, 3).map(task => (
                          <div key={task.id} className={`mini-task ${task.completed ? 'completed' : ''}`}>
                            {task.text.length > 15 ? task.text.substring(0, 15) + '...' : task.text}
                          </div>
                        ))}
                        {dayTasks.length > 3 && (
                          <div className="more-tasks">+{dayTasks.length - 3}</div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Reflections Section */}
            <div className="reflections-section">
              <div className="reflections-header">
                <h3>Рефлексия</h3>
                <button onClick={() => setShowReflectionModal(true)} className="btn btn-secondary">Записать размышления</button>
              </div>
              
              <div className="reflections-list">
                {weeklyReflections.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните записывать свои размышления!</p>
                  </div>
                ) : (
                  weeklyReflections.slice(0, 3).map(reflection => (
                    <div key={reflection.id} className="reflection-item">
                      <div className="reflection-date">{reflection.week}</div>
                      <p className="reflection-text">{reflection.reflection}</p>
                      {reflection.improvements && (
                        <div className="improvements">
                          <strong>Улучшения:</strong> {reflection.improvements}
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Habits Section */}
            <div className="habits-section">
              <div className="habits-header">
                <h3>Привычки</h3>
                <button onClick={() => setShowHabitsModal(true)} className="btn btn-secondary">Добавить привычку</button>
              </div>
              
              <div className="habits-list">
                {habits.length === 0 ? (
                  <div className="empty-state">
                    <p>Создайте полезные привычки!</p>
                  </div>
                ) : (
                  habits.map(habit => (
                    <div key={habit.id} className="habit-item">
                      <button
                        onClick={() => toggleHabit(habit.id)}
                        className={`habit-checkbox ${habit.completed ? 'completed' : ''}`}
                      >
                        {habit.completed && '✓'}
                      </button>
                      <div className="habit-info">
                        <h4>{habit.name}</h4>
                        <p>{habit.description}</p>
                        <div className="habit-meta">
                          <span className="frequency">{habit.frequency}</span>
                          <span className="streak">🔥 {habit.streak} дней</span>
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">Финансовое планирование</h2>
            
            {/* Budget Section */}
            <div className="budget-section">
              <div className="budget-header">
                <h3>Бюджет</h3>
                <button onClick={() => setShowBudgetModal(true)} className="btn btn-primary">Добавить запись</button>
              </div>
              
              <div className="budget-summary">
                <div className="budget-total">
                  <span className="total-label">Баланс:</span>
                  <span className={`total-amount ${budget.reduce((sum, item) => sum + (item.type === 'income' ? item.amount : -item.amount), 0) >= 0 ? 'positive' : 'negative'}`}>
                    {budget.reduce((sum, item) => sum + (item.type === 'income' ? item.amount : -item.amount), 0).toFixed(2)} ₽
                  </span>
                </div>
                <div className="budget-breakdown">
                  <div className="income">
                    <span>Доходы: {budget.filter(item => item.type === 'income').reduce((sum, item) => sum + item.amount, 0).toFixed(2)} ₽</span>
                  </div>
                  <div className="expenses">
                    <span>Расходы: {budget.filter(item => item.type === 'expense').reduce((sum, item) => sum + item.amount, 0).toFixed(2)} ₽</span>
                  </div>
                </div>
              </div>
              
              <div className="budget-list">
                {budget.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните вести учет доходов и расходов!</p>
                  </div>
                ) : (
                  budget.slice(0, 5).map(item => (
                    <div key={item.id} className={`budget-item ${item.type}`}>
                      <div className="budget-info">
                        <span className="budget-category">{item.category}</span>
                        <span className="budget-description">{item.description}</span>
                      </div>
                      <span className={`budget-amount ${item.type}`}>
                        {item.type === 'income' ? '+' : '-'}{item.amount} ₽
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Shopping Lists Section */}
            <div className="shopping-section">
              <div className="shopping-header">
                <h3>Списки покупок</h3>
                <button onClick={() => setShowShoppingModal(true)} className="btn btn-primary">Создать список</button>
              </div>
              
              <div className="shopping-lists">
                {shoppingLists.length === 0 ? (
                  <div className="empty-state">
                    <p>Создайте свой первый список покупок!</p>
                  </div>
                ) : (
                  shoppingLists.slice(0, 3).map(list => (
                    <div key={list.id} className="shopping-list-item">
                      <div className="list-header">
                        <h4>{list.name}</h4>
                        <span className="list-count">{list.items.filter(item => !item.completed).length} осталось</span>
                      </div>
                      <div className="list-items">
                        {list.items.slice(0, 3).map(item => (
                          <div key={item.id} className={`list-item ${item.completed ? 'completed' : ''}`}>
                            <span>{item.name}</span>
                            {item.price && <span className="item-price">{item.price} ₽</span>}
                          </div>
                        ))}
                        {list.items.length > 3 && (
                          <div className="more-items">+{list.items.length - 3} еще</div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">Путешествия</h2>
            
            {/* Travel Planning Section */}
            <div className="travel-section">
              <div className="travel-header">
                <h3>Планирование поездок</h3>
                <button onClick={() => setShowTravelModal(true)} className="btn btn-primary">Планировать поездку</button>
              </div>
              
              <div className="trips-list">
                {trips.length === 0 ? (
                  <div className="empty-state">
                    <p>Начните планировать свои путешествия!</p>
                  </div>
                ) : (
                  trips.map(trip => (
                    <div key={trip.id} className="trip-item">
                      <div className="trip-header">
                        <div className="trip-info">
                          <h4>{trip.title}</h4>
                          <p className="trip-destination">📍 {trip.destination}</p>
                          <div className="trip-dates">
                            <span>{new Date(trip.startDate).toLocaleDateString('ru-RU')} - {new Date(trip.endDate).toLocaleDateString('ru-RU')}</span>
                          </div>
                          {trip.budget > 0 && (
                            <div className="trip-budget">
                              💰 Бюджет: {trip.budget.toLocaleString()} ₽
                            </div>
                          )}
                        </div>
                        <div className="trip-status">
                          <span className={`status-badge ${new Date(trip.startDate) > new Date() ? 'upcoming' : new Date(trip.endDate) < new Date() ? 'completed' : 'current'}`}>
                            {new Date(trip.startDate) > new Date() ? 'Предстоящая' : new Date(trip.endDate) < new Date() ? 'Завершена' : 'Текущая'}
                          </span>
                        </div>
                      </div>
                      
                      {trip.checklist.length > 0 && (
                        <div className="trip-checklist">
                          <h5>Список дел:</h5>
                          <div className="checklist-items">
                            {trip.checklist.slice(0, 3).map(item => (
                              <div key={item.id} className={`checklist-item ${item.completed ? 'completed' : ''}`}>
                                <button
                                  onClick={() => toggleChecklistItem(trip.id, item.id)}
                                  className={`checklist-checkbox ${item.completed ? 'checked' : ''}`}
                                >
                                  {item.completed && '✓'}
                                </button>
                                <span>{item.item}</span>
                              </div>
                            ))}
                            {trip.checklist.length > 3 && (
                              <div className="more-items">+{trip.checklist.length - 3} еще</div>
                            )}
                          </div>
                        </div>
                      )}

                      {trip.itinerary.length > 0 && (
                        <div className="trip-itinerary">
                          <h5>Маршрут:</h5>
                          <div className="itinerary-items">
                            {trip.itinerary.slice(0, 2).map(day => (
                              <div key={day.id} className="itinerary-item">
                                <div className="itinerary-day">День {day.day}</div>
                                <div className="itinerary-activity">{day.activity}</div>
                                {day.time && <div className="itinerary-time">🕒 {day.time}</div>}
                                {day.location && <div className="itinerary-location">📍 {day.location}</div>}
                              </div>
                            ))}
                            {trip.itinerary.length > 2 && (
                              <div className="more-items">+{trip.itinerary.length - 2} дней еще</div>
                            )}
                          </div>
                        </div>
                      )}

                      {trip.notes && (
                        <div className="trip-notes">
                          <p>📝 {trip.notes}</p>
                        </div>
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">Челленджи и достижения</h2>
            
            {/* Challenges Section */}
            <div className="challenges-section">
              <div className="challenges-header">
                <h3>Еженедельные челленджи</h3>
              </div>
              
              <div className="challenges-list">
                {challenges.map(challenge => (
                  <div key={challenge.id} className={`challenge-item ${challenge.completed ? 'completed' : ''}`}>
                    <div className="challenge-header">
                      <div className="challenge-info">
                        <h4>{challenge.title}</h4>
                        <p>{challenge.description}</p>
                      </div>
                      <div className="challenge-reward">+{challenge.reward} очков</div>
                    </div>
                    
                    <div className="challenge-progress">
                      <div className="progress-bar">
                        <div 
                          className="progress-fill" 
                          style={{width: `${(challenge.current / challenge.target) * 100}%`}}
                        ></div>
                      </div>
                      <div className="progress-text">
                        {challenge.current} / {challenge.target}
                      </div>
                    </div>
                    
                    {challenge.completed && (
                      <div className="challenge-completed">
                        <span className="completed-icon">✅</span>
                        <span>Выполнено!</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Achievements Section */}
            <div className="achievements-section">
              <div className="achievements-header">
                <h3>Все достижения</h3>
              </div>
              
              <div className="achievements-grid">
                {achievements.map(achievement => (
                  <div key={achievement.id} className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}>
                    <div className="achievement-icon-large">{achievement.icon}</div>
                    <div className="achievement-content">
                      <h4>{achievement.title}</h4>
                      <p>{achievement.description}</p>
                      <div className="achievement-meta">
                        <span className="achievement-points">{achievement.points} очков</span>
                        {achievement.unlocked && (
                          <span className="achievement-date">
                            {new Date(achievement.unlockedDate!).toLocaleDateString('ru-RU')}
                          </span>
                        )}
                      </div>
                      {achievement.unlocked && socialSharing.shareAchievements && (
                        <div className="achievement-sharing">
                          <button 
                            onClick={() => shareAchievement('instagram', achievement)}
                            className="share-btn instagram"
                            title="Поделиться в Instagram"
                          >
                            📷
                          </button>
                          <button 
                            onClick={() => shareAchievement('facebook', achievement)}
                            className="share-btn facebook"
                            title="Поделиться в Facebook"
                          >
                            📘
                          </button>
                          <button 
                            onClick={() => shareAchievement('twitter', achievement)}
                            className="share-btn twitter"
                            title="Поделиться в Twitter"
                          >
                            🐦
                          </button>
                          <button 
                            onClick={() => shareAchievement('telegram', achievement)}
                            className="share-btn telegram"
                            title="Поделиться в Telegram"
                          >
                            📱
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="section">
            <h2 className="section-title">Интеграции</h2>
            
            {/* Calendar Integration */}
            <div className="integration-section">
              <div className="integration-header">
                <h3>📅 Календарь</h3>
              </div>
              
              <div className="integration-grid">
                <div className="integration-card">
                  <div className="integration-info">
                    <div className="integration-icon">📊</div>
                    <div className="integration-details">
                      <h4>Google Calendar</h4>
                      <p>Синхронизация событий и встреч</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => syncWithCalendar('google')}
                    className={`integration-btn ${calendarIntegration.googleCalendar ? 'connected' : ''}`}
                  >
                    {calendarIntegration.googleCalendar ? 'Подключено' : 'Подключить'}
                  </button>
                </div>

                <div className="integration-card">
                  <div className="integration-info">
                    <div className="integration-icon">🍎</div>
                    <div className="integration-details">
                      <h4>Apple Calendar</h4>
                      <p>Интеграция с календарем Apple</p>
                    </div>
                  </div>
                  <button 
                    onClick={() => syncWithCalendar('apple')}
                    className={`integration-btn ${calendarIntegration.appleCalendar ? 'connected' : ''}`}
                  >
                    {calendarIntegration.appleCalendar ? 'Подключено' : 'Подключить'}
                  </button>
                </div>
              </div>
            </div>

            {/* Fitness Integration */}
            <div className="integration-section">
              <div className="integration-header">
                <h3>💪 Фитнес-трекеры</h3>
              </div>
              
              <div className="integration-grid">
                <div className="integration-card">
                  <div className="integration-info">
                    <div className="integration-icon">🍎</div>
                    <div className="integration-details">
                      <h4>Apple Health</h4>
                      <p>Шаги, пульс, сон, активность</p>
                      {fitnessIntegration.appleHealth && (
                        <div className="sync-status">
                          <span className="sync-indicator">🔄</span>
                          <span>Синхронизация активна</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => syncWithFitness('apple')}
                    className={`integration-btn ${fitnessIntegration.appleHealth ? 'connected' : ''}`}
                  >
                    {fitnessIntegration.appleHealth ? 'Подключено' : 'Подключить'}
                  </button>
                </div>

                <div className="integration-card">
                  <div className="integration-info">
                    <div className="integration-icon">🏃</div>
                    <div className="integration-details">
                      <h4>Google Fit</h4>
                      <p>Активность, тренировки, здоровье</p>
                      {fitnessIntegration.googleFit && (
                        <div className="sync-status">
                          <span className="sync-indicator">🔄</span>
                          <span>Синхронизация активна</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <button 
                    onClick={() => syncWithFitness('google')}
                    className={`integration-btn ${fitnessIntegration.googleFit ? 'connected' : ''}`}
                  >
                    {fitnessIntegration.googleFit ? 'Подключено' : 'Подключить'}
                  </button>
                </div>
              </div>
            </div>

            {/* Weather Integration */}
            <div className="integration-section">
              <div className="integration-header">
                <h3>🌤️ Погода</h3>
              </div>
              
              <div className="weather-widget">
                <div className="current-weather">
                  <div className="weather-main">
                    <div className="weather-icon">{getWeatherIcon(weatherData.condition)}</div>
                    <div className="weather-info">
                      <div className="weather-temp">{weatherData.currentTemp}°C</div>
                      <div className="weather-condition">{weatherData.condition}</div>
                      <div className="weather-location">📍 {weatherData.location}</div>
                    </div>
                  </div>
                  <div className="weather-forecast">
                    <h4>Прогноз на 3 дня:</h4>
                    <div className="forecast-list">
                      {weatherData.forecast.map((day, index) => (
                        <div key={index} className="forecast-item">
                          <span className="forecast-day">{day.day}</span>
                          <span className="forecast-icon">{getWeatherIcon(day.condition)}</span>
                          <span className="forecast-temp">{day.temp}°C</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="weather-tips">
                  <h4>💡 Рекомендации:</h4>
                  <ul>
                    <li>Отличная погода для прогулки!</li>
                    <li>Не забудьте солнцезащитный крем</li>
                    <li>Идеально для занятий спортом на свежем воздухе</li>
                  </ul>
                </div>
              </div>
            </div>

            {/* Social Sharing */}
            <div className="integration-section">
              <div className="integration-header">
                <h3>📱 Социальные сети</h3>
              </div>
              
              <div className="social-sharing-settings">
                <div className="sharing-options">
                  <label className="sharing-option">
                    <input
                      type="checkbox"
                      checked={socialSharing.shareAchievements}
                      onChange={(e) => setSocialSharing(prev => ({...prev, shareAchievements: e.target.checked}))}
                    />
                    <span>Делиться достижениями</span>
                  </label>
                  <label className="sharing-option">
                    <input
                      type="checkbox"
                      checked={socialSharing.shareProgress}
                      onChange={(e) => setSocialSharing(prev => ({...prev, shareProgress: e.target.checked}))}
                    />
                    <span>Делиться прогрессом</span>
                  </label>
                  <label className="sharing-option">
                    <input
                      type="checkbox"
                      checked={socialSharing.shareGoals}
                      onChange={(e) => setSocialSharing(prev => ({...prev, shareGoals: e.target.checked}))}
                    />
                    <span>Делиться целями</span>
                  </label>
                </div>
                
                <div className="platforms-section">
                  <h4>Выберите платформы:</h4>
                  <div className="platforms-grid">
                    {Object.entries(socialSharing.platforms).map(([platform, enabled]) => (
                      <label key={platform} className="platform-option">
                        <input
                          type="checkbox"
                          checked={enabled}
                          onChange={(e) => setSocialSharing(prev => ({
                            ...prev,
                            platforms: {
                              ...prev.platforms,
                              [platform]: e.target.checked
                            }
                          }))}
                        />
                        <span className={`platform-icon ${platform}`}>
                          {platform === 'instagram' ? '📷' :
                           platform === 'facebook' ? '📘' :
                           platform === 'twitter' ? '🐦' : '📱'}
                        </span>
                        <span className="platform-name">
                          {platform === 'instagram' ? 'Instagram' :
                           platform === 'facebook' ? 'Facebook' :
                           platform === 'twitter' ? 'Twitter' : 'Telegram'}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Goals Modal */}
      {showGoalsModal && (
        <div className="modal-overlay" onClick={() => setShowGoalsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить цель</h3>
              <button onClick={() => setShowGoalsModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название цели"
                value={newGoal.title}
                onChange={(e) => setNewGoal({...newGoal, title: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Описание цели"
                value={newGoal.description}
                onChange={(e) => setNewGoal({...newGoal, description: e.target.value})}
                className="modal-textarea"
              />
              <div className="steps-section">
                <h4>Шаги для достижения:</h4>
                {newGoal.steps.map((step, index) => (
                  <input
                    key={index}
                    type="text"
                    placeholder={`Шаг ${index + 1}`}
                    value={step}
                    onChange={(e) => updateGoalStep(index, e.target.value)}
                    className="modal-input"
                  />
                ))}
                <button onClick={addGoalStep} className="btn btn-secondary">Добавить шаг</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowGoalsModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={addGoal} className="btn btn-primary">Добавить цель</button>
            </div>
          </div>
        </div>
      )}

      {/* Reflection Modal */}
      {showReflectionModal && (
        <div className="modal-overlay" onClick={() => setShowReflectionModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Недельная рефлексия</h3>
              <button onClick={() => setShowReflectionModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <textarea
                placeholder="Как прошла неделя? Что получилось хорошо?"
                value={newReflection.reflection}
                onChange={(e) => setNewReflection({...newReflection, reflection: e.target.value})}
                className="modal-textarea"
                rows={4}
              />
              <textarea
                placeholder="Что можно улучшить на следующей неделе?"
                value={newReflection.improvements}
                onChange={(e) => setNewReflection({...newReflection, improvements: e.target.value})}
                className="modal-textarea"
                rows={3}
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowReflectionModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={addReflection} className="btn btn-primary">Сохранить</button>
            </div>
          </div>
        </div>
      )}

      {/* Habits Modal */}
      {showHabitsModal && (
        <div className="modal-overlay" onClick={() => setShowHabitsModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить привычку</h3>
              <button onClick={() => setShowHabitsModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название привычки"
                value={newHabit.name}
                onChange={(e) => setNewHabit({...newHabit, name: e.target.value})}
                className="modal-input"
              />
              <textarea
                placeholder="Описание привычки"
                value={newHabit.description}
                onChange={(e) => setNewHabit({...newHabit, description: e.target.value})}
                className="modal-textarea"
              />
              <select
                value={newHabit.frequency}
                onChange={(e) => setNewHabit({...newHabit, frequency: e.target.value})}
                className="modal-select"
              >
                <option value="daily">Ежедневно</option>
                <option value="weekly">Еженедельно</option>
                <option value="monthly">Ежемесячно</option>
              </select>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowHabitsModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={addHabit} className="btn btn-primary">Добавить привычку</button>
            </div>
          </div>
        </div>
      )}

      {/* Budget Modal */}
      {showBudgetModal && (
        <div className="modal-overlay" onClick={() => setShowBudgetModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Добавить запись в бюджет</h3>
              <button onClick={() => setShowBudgetModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <select
                value={newBudgetItem.type}
                onChange={(e) => setNewBudgetItem({...newBudgetItem, type: e.target.value as 'income' | 'expense'})}
                className="modal-select"
              >
                <option value="expense">Расход</option>
                <option value="income">Доход</option>
              </select>
              <input
                type="text"
                placeholder="Категория"
                value={newBudgetItem.category}
                onChange={(e) => setNewBudgetItem({...newBudgetItem, category: e.target.value})}
                className="modal-input"
              />
              <input
                type="number"
                placeholder="Сумма"
                value={newBudgetItem.amount}
                onChange={(e) => setNewBudgetItem({...newBudgetItem, amount: e.target.value})}
                className="modal-input"
              />
              <input
                type="text"
                placeholder="Описание"
                value={newBudgetItem.description}
                onChange={(e) => setNewBudgetItem({...newBudgetItem, description: e.target.value})}
                className="modal-input"
              />
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowBudgetModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={addBudgetItem} className="btn btn-primary">Добавить</button>
            </div>
          </div>
        </div>
      )}

      {/* Shopping List Modal */}
      {showShoppingModal && (
        <div className="modal-overlay" onClick={() => setShowShoppingModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Создать список покупок</h3>
              <button onClick={() => setShowShoppingModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <input
                type="text"
                placeholder="Название списка"
                value={newShoppingList.name}
                onChange={(e) => setNewShoppingList({...newShoppingList, name: e.target.value})}
                className="modal-input"
              />
              <div className="items-section">
                <h4>Товары:</h4>
                {newShoppingList.items.map((item, index) => (
                  <div key={index} className="item-input-row">
                    <input
                      type="text"
                      placeholder="Название товара"
                      value={item.name}
                      onChange={(e) => updateShoppingItem(index, 'name', e.target.value)}
                      className="modal-input"
                    />
                    <input
                      type="number"
                      placeholder="Цена"
                      value={item.price}
                      onChange={(e) => updateShoppingItem(index, 'price', e.target.value)}
                      className="modal-input"
                    />
                  </div>
                ))}
                <button onClick={addShoppingItem} className="btn btn-secondary">Добавить товар</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowShoppingModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={addShoppingList} className="btn btn-primary">Создать список</button>
            </div>
          </div>
        </div>
      )}

      {/* Travel Planning Modal */}
      {showTravelModal && (
        <div className="modal-overlay" onClick={() => setShowTravelModal(false)}>
          <div className="modal travel-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Планирование поездки</h3>
              <button onClick={() => setShowTravelModal(false)} className="close-btn">×</button>
            </div>
            <div className="modal-body">
              <div className="form-row">
                <input
                  type="text"
                  placeholder="Название поездки"
                  value={newTrip.title}
                  onChange={(e) => setNewTrip({...newTrip, title: e.target.value})}
                  className="modal-input"
                />
                <input
                  type="text"
                  placeholder="Место назначения"
                  value={newTrip.destination}
                  onChange={(e) => setNewTrip({...newTrip, destination: e.target.value})}
                  className="modal-input"
                />
              </div>
              
              <div className="form-row">
                <input
                  type="date"
                  placeholder="Дата начала"
                  value={newTrip.startDate}
                  onChange={(e) => setNewTrip({...newTrip, startDate: e.target.value})}
                  className="modal-input"
                />
                <input
                  type="date"
                  placeholder="Дата окончания"
                  value={newTrip.endDate}
                  onChange={(e) => setNewTrip({...newTrip, endDate: e.target.value})}
                  className="modal-input"
                />
              </div>
              
              <input
                type="number"
                placeholder="Бюджет поездки (₽)"
                value={newTrip.budget}
                onChange={(e) => setNewTrip({...newTrip, budget: e.target.value})}
                className="modal-input"
              />
              
              <textarea
                placeholder="Заметки о поездке"
                value={newTrip.notes}
                onChange={(e) => setNewTrip({...newTrip, notes: e.target.value})}
                className="modal-textarea"
                rows={3}
              />

              <div className="checklist-section">
                <h4>Список дел:</h4>
                {newTrip.checklist.map((item, index) => (
                  <div key={index} className="checklist-input-row">
                    <input
                      type="text"
                      placeholder="Что нужно сделать"
                      value={item.item}
                      onChange={(e) => updateChecklistItem(index, e.target.value)}
                      className="modal-input"
                    />
                  </div>
                ))}
                <button onClick={addChecklistItem} className="btn btn-secondary">Добавить пункт</button>
              </div>

              <div className="itinerary-section">
                <h4>Маршрут по дням:</h4>
                {newTrip.itinerary.map((day, index) => (
                  <div key={index} className="itinerary-input-row">
                    <div className="form-row">
                      <input
                        type="number"
                        placeholder="День"
                        value={day.day}
                        onChange={(e) => updateItineraryItem(index, 'day', e.target.value)}
                        className="modal-input"
                        min="1"
                      />
                      <input
                        type="text"
                        placeholder="Время"
                        value={day.time}
                        onChange={(e) => updateItineraryItem(index, 'time', e.target.value)}
                        className="modal-input"
                      />
                    </div>
                    <input
                      type="text"
                      placeholder="Активность"
                      value={day.activity}
                      onChange={(e) => updateItineraryItem(index, 'activity', e.target.value)}
                      className="modal-input"
                    />
                    <input
                      type="text"
                      placeholder="Место"
                      value={day.location}
                      onChange={(e) => updateItineraryItem(index, 'location', e.target.value)}
                      className="modal-input"
                    />
                    <input
                      type="text"
                      placeholder="Заметки"
                      value={day.notes}
                      onChange={(e) => updateItineraryItem(index, 'notes', e.target.value)}
                      className="modal-input"
                    />
                  </div>
                ))}
                <button onClick={addItineraryDay} className="btn btn-secondary">Добавить день</button>
              </div>
            </div>
            <div className="modal-footer">
              <button onClick={() => setShowTravelModal(false)} className="btn btn-secondary">Отмена</button>
              <button onClick={addTrip} className="btn btn-primary">Создать поездку</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlannerPage;
